import { Divider, Grid, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React, { useMemo, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import { DataAPIContext } from '../api';
import { GET_ONE, UPDATE_BATCH } from '../api/dataProvider';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import TagEdit from './TagEdit';
import TagListToolbar from './TagListToolbar';
import { categoryFromSearch, searchWithCategory } from './categoryParam';
import { ConfirmationDialogText } from './utils';
import ResizableSidebar from '../components/ResizableSidebar';

function useIsWidthUp(breakpoint) {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.up(breakpoint));
}

const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: 0,
        margin: theme.spacing(2),
        marginTop: '0.5rem',
        height: 'calc(100svh - 7rem)',
        display: 'flex',
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100svh - 5rem)',
        },
    },
    gridRoot: {},
    recordList: {
        marginTop: theme.spacing(2),
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    sortButton: {
        minWidth: theme.spacing(4),
        minHeight: theme.spacing(4),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        padding: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    progress: {
        marginBottom: theme.spacing(1),
    },
    listItem: {
        height: '100%',
        maxHeight: '300px',
    },
}));

const TagList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const urlParams = useParams();
    const history = useHistory();
    const location = useLocation();
    const classes = useStyles();
    const resource = 'tour_tag';
    const [query, setQuery] = React.useState(null);
    const recordListRef = useRef();
    const [syncActive, setSyncActive] = React.useState(false);
    const [activeTag, setActiveTag] = React.useState(null);
    const [detailsDirty, setDetailsDirty] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [newRowOrder, setNewRowOrder] = React.useState({});
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    // The category filter lives in the URL (`/tags?category=<id>`, linked from Developer › Integrations), so
    // a link opens the list filtered and the browser's back and forward follow it. In-page navigation keeps it.
    const tagCategoryListFilter = categoryFromSearch(location.search);
    const setTagCategoryListFilter = (id) => history.replace({ pathname: location.pathname, search: searchWithCategory(location.search, id) });
    const tagsPath = (pathname) => ({ pathname, search: location.search });
    const [tagCategories, setTagCategories] = React.useState();

    const [selectedIds, setSelectedIds] = React.useState([]);

    React.useEffect(() => {
        const getAvailableCategories = async () => {
            let params = {};
            params.urlSuffix = null;
            const res = await dataAPI('GET_LIST', 'tour_tag_category', params);
            if (res.data) {
                setTagCategories(res.data);
            }
        };
        getAvailableCategories();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSelectionModelChange = (ids, selection) => {
        setSelectedIds(selection);
        if (!hasPerm('tag', 'read')) {
            return
        }
        if (selection.length === 1) {
            history.push(tagsPath(`/tags/${selection[0]}`));
        } else {
            history.push(tagsPath('/tags'));
            setActiveTag(null);
        }
    };

    // CP-7771: one batch request, run as a background task (it was one DELETE per tag); the grid
    // refreshes once the task has finished. Tags still in use are retired, as the single delete does.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['tag', 'tags'], onDeleted: refreshList });
    const doDeleteItemList = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setIdsToDelete(null);
                    setActiveTag(null);
                }
            })
            .finally(() => {
                setSelectedIds([]);
                setSyncActive(false);
            });
    };

    const onDeleteItemList = (idlist) => {
        setIdsToDelete(idlist);
        setConfirmationDialogAction(idlist.length > 1 ? 'delete_tags' : 'delete_tag');
    };

    const onItemDelete = (f) => {
        return doDeleteItemList([f.id]);
    };

    const onSaveNewOrder = (ev) => {
        setSyncActive(true);
        dataAPI(UPDATE_BATCH, resource, { data: newRowOrder, urlSuffix: 'reorder/' })
            .then((res) => {
                console.log('reorder res', res);
                if (res.statusCode < 400) {
                    enqueueSnackbar('New tag saved', { variant: 'success' });
                    setLastUpdate(new Date().toString());
                    setNewRowOrder({});
                } else {
                    enqueueSnackbar('Error saving new region order', { variant: 'error' });
                }
            })
            .catch((err) => {
                enqueueSnackbar('Error saving new region order', { variant: 'error' });
            })
            .finally(() => {
                setSyncActive(false);
            });
    };

    const onRowOrderUpdate = (upd) => {
        const newOrder = { ...newRowOrder, ...upd };
        console.log('newOrder', newOrder);
        setNewRowOrder(newOrder);
    };

    const onRowClick = (p) => {
        if (!hasPerm('tag', 'read')) {
            return
        }

        if (!detailsDirty && isSmallScreen) {
            setDetailsDirty(false);
            history.push(tagsPath(`/tags/${p.row.id}`));
        }
    };

    const onDetailsClose = (ev) => {
        setActiveTag(null);
        setDetailsDirty(false);
        history.push(tagsPath('/tags'));
    };

    const onTagSave = () => {
        setLastUpdate(new Date().toString());
        setDetailsDirty(false);
    };

    React.useEffect(() => {
        if (!urlParams || !urlParams.id) {
            return;
        }
        dataAPI(GET_ONE, resource, { id: urlParams.id }).then((res) => {
            console.log('get one', urlParams.id, res);
            setActiveTag(res.data);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParams.id]);

    const listItemRenderer = (row, idx, rows, gridApi) => {
        return (
            <React.Fragment>
                <ListItem alignItems="flex-start" className={classes.listItem} onClick={(ev) => gridApi.onRowClick({ row, idx }, ev)}>
                    <ListItemText primary={row.name} secondary={row.category.name} />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    // cols больше не используется на десктопе: заменено на ResizableSidebar

    const apiFilters = useMemo(() => ([
        { field: 'category', op: 'eq', value: tagCategoryListFilter },
    ]), [tagCategoryListFilter]);

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_tag':
            case 'delete_tags':
                doDeleteItemList(idsToDelete);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };


    const replaceTagCategoryIdByName = (id) => {
        let found_cat;
        tagCategories?.map((cat) => {
            if (cat.id === id) {
                found_cat = cat.name;
            }
            return found_cat;
        });
        return found_cat;
    };

    const onExportList = async () => {
        try {
            setSyncActive(true);
            const params = {
                filter: {
                    query: query,
                    filters: JSON.stringify(apiFilters),
                },
                urlSuffix: 'export/',
            };

            const res = await dataAPI(GET_ONE, resource, params);
            const body = await res.response.blob();
            const url = window.URL.createObjectURL(body);

            let filename = '';
            const disposition = res.headers['Content-Disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/["']/g, '');
                }
            }
            if (!filename || filename.length <= 0) {
                filename = 'tags.xlsx';
            }

            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            setTimeout(() => {
                document.body.removeChild(a);
            }, 100);
        } catch (error) {
            console.error('onExportList: Error exporting tags list:', error);
        } finally {
            setSyncActive(false);
        }
    };

    return (
        <div className={classes.root}>
            <PageMeta title="Tags" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            {isSmallScreen ? (
                <Grid container direction="row" spacing={2}>
                    {isSmallScreen && activeTag ? null : (
                        <Grid item xs={12} style={{ height: "calc(100vh - 12.5rem)" }}>
                            <PageHeading sx={{ mt: 1, mb: 1 }}>Tags</PageHeading>
                            <TagListToolbar
                                resource={resource}
                                selectedIds={selectedIds}
                                onQueryChange={setQuery}
                                onDeleteItem={onDeleteItemList}
                                newRowOrder={newRowOrder}
                                onSaveNewOrder={onSaveNewOrder}
                                isSmallScreen={isSmallScreen}
                                tagCategoryListFilter={tagCategoryListFilter}
                                setTagCategoryListFilter={setTagCategoryListFilter}
                                setLastUpdate={setLastUpdate}
                                onExportList={onExportList}
                            />
                            <RecordList
                                ref={recordListRef}
                                className={classes.recordList}
                                resource={resource}
                                checkboxSelection={hasPerm('tag', 'read')}
                                disableSelectionOnClick={!hasPerm('tag', 'read')}
                                onSelectionModelChange={onSelectionModelChange}
                                onRowClick={onRowClick}
                                deps={lastUpdate}
                                onQueryChange={setQuery}
                                query={query}
                                apiFilters={apiFilters}
                                perPage={100}
                                onRowOrderUpdate={onRowOrderUpdate}
                                listItemRenderer={listItemRenderer}
                                columns={[
                                    ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                                    { field: 'name', headerName: 'Name', sortable: false, flex: 1 },
                                    { field: 'category', headerName: 'Category',  sortable: false, filterable: false, flex: 0.5, valueGetter: (p) => (p.row.category ? (p.row.category.constructor === Object ? p.row.category.name : replaceTagCategoryIdByName(p.row.category)) : '[not set]') },
                                    { field: 'user', headerName: 'Associated User', sortable: false,filterable: false, flex: 0.5, valueGetter: (p) => (p.row.user ? p.row.user.email : '') },
                                    // Hidden by default: NULL for every operator-authored tag, and only
                    // externally identified categories fill it. RecordList seeds its
                    // columnVisibilityModel from `hide`, so a user's choice persists.
                    { field: 'custom_id', headerName: 'External ID', sortable: false, filterable: false, flex: 0.5, hide: true, valueGetter: (p) => (p.row.custom_id || '') },
                    { field: 'friendly_name', headerName: 'Friendly Name', sortable: false, filterable: false, flex: 0.5, valueGetter: (p) => (p.row.data?.friendly_name ? p.row.data.friendly_name : '') },
                                    { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', ') },
                                ]}
                            />
                        </Grid>
                    )}
                    {activeTag ? (
                        <Grid item xs={12}>
                            <TagEdit resource={resource} data={activeTag} onClose={onDetailsClose} onChange={onTagSave} onDelete={onItemDelete} syncActive={syncActive} setSyncActive={setSyncActive} />
                        </Grid>
                    ) : null}
                </Grid>
            ) : (
                <ResizableSidebar
                    storageKey="tags.sidebar.width"
                    showRight={!!activeTag}
                    left={
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <PageHeading sx={{ mt: 1, mb: 1 }}>Tags</PageHeading>
                            <TagListToolbar
                                resource={resource}
                                selectedIds={selectedIds}
                                onQueryChange={setQuery}
                                onDeleteItem={onDeleteItemList}
                                newRowOrder={newRowOrder}
                                onSaveNewOrder={onSaveNewOrder}
                                isSmallScreen={isSmallScreen}
                                tagCategoryListFilter={tagCategoryListFilter}
                                setTagCategoryListFilter={setTagCategoryListFilter}
                                setLastUpdate={setLastUpdate}
                                onExportList={onExportList}
                            />
                            <RecordList
                                ref={recordListRef}
                                className={classes.recordList}
                                resource={resource}
                                checkboxSelection={hasPerm('tag', 'read')}
                                disableSelectionOnClick={!hasPerm('tag', 'read')}
                                onSelectionModelChange={onSelectionModelChange}
                                onRowClick={onRowClick}
                                deps={lastUpdate}
                                onQueryChange={setQuery}
                                query={query}
                                apiFilters={apiFilters}
                                perPage={100}
                                onRowOrderUpdate={onRowOrderUpdate}
                                listItemRenderer={listItemRenderer}
                                columns={[
                                    ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                                    { field: 'name', headerName: 'Name', sortable: false, flex: 1 },
                                    { field: 'category', headerName: 'Category',  sortable: false, filterable: false, flex: 0.5, valueGetter: (p) => (p.row.category ? (p.row.category.constructor === Object ? p.row.category.name : replaceTagCategoryIdByName(p.row.category)) : '[not set]') },
                                    { field: 'user', headerName: 'Associated User', sortable: false,filterable: false, flex: 0.5, valueGetter: (p) => (p.row.user ? p.row.user.email : '') },
                                    // Hidden by default: NULL for every operator-authored tag, and only
                    // externally identified categories fill it. RecordList seeds its
                    // columnVisibilityModel from `hide`, so a user's choice persists.
                    { field: 'custom_id', headerName: 'External ID', sortable: false, filterable: false, flex: 0.5, hide: true, valueGetter: (p) => (p.row.custom_id || '') },
                    { field: 'friendly_name', headerName: 'Friendly Name', sortable: false, filterable: false, flex: 0.5, valueGetter: (p) => (p.row.data?.friendly_name ? p.row.data.friendly_name : '') },
                                    { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', ') },
                                ]}
                            />
                        </div>
                    }
                    right={
                        <TagEdit
                            resource={resource}
                            data={activeTag}
                            onClose={onDetailsClose}
                            onChange={onTagSave}
                            onDelete={onItemDelete}
                            syncActive={syncActive}
                            setSyncActive={setSyncActive}
                        />
                    }
                />
            )}
        </div>
    );
};

export default TagList;
